/**
 * Where Vérifications is, from the URL hash, so every view is a link that can
 * be shared:
 *
 * - `#verifications` is the queue of open verifications (submitted and flagged);
 * - `#verifications?status=flagged&type=passport` is the queue, filtered;
 * - `#verifications/<id>` opens one verification next to the queue, keeping the filters.
 *
 * Only ids and filters ever go in the hash: never an image URL.
 */
import {
  ID_DOCUMENT_TYPES,
  VERIFICATION_STATUSES,
  type IdDocumentType,
  type ListVerificationsRequest,
  type VerificationStatus,
} from "@wallyo/contracts/console/verifications";

export const STATUS_FILTERS = ["open", ...VERIFICATION_STATUSES] as const;
export type StatusFilter = (typeof STATUS_FILTERS)[number];

export const DOCUMENT_FILTERS = ["all", ...ID_DOCUMENT_TYPES] as const;
export type DocumentFilter = (typeof DOCUMENT_FILTERS)[number];

export type VerificationFilters = { status: StatusFilter; documentType: DocumentFilter };

export const DEFAULT_FILTERS: VerificationFilters = { status: "open", documentType: "all" };

export type VerificationsRoute = { filters: VerificationFilters; selectedId: string | null };

const isStatus = (value: string | null): value is StatusFilter => STATUS_FILTERS.includes(value as StatusFilter);
const isDocument = (value: string | null): value is DocumentFilter =>
  DOCUMENT_FILTERS.includes(value as DocumentFilter);

export function parseVerificationsHash(hash: string): VerificationsRoute {
  const rest = hash.replace(/^#\/?verifications/, "");
  const [path = "", search = ""] = rest.split("?", 2);
  const params = new URLSearchParams(search);
  const status = params.get("status");
  const type = params.get("type");
  let selectedId: string | null = null;
  try {
    selectedId = decodeURIComponent(path.replace(/^\/+/, "").split("/")[0] ?? "").trim() || null;
  } catch {
    selectedId = null;
  }
  return {
    filters: {
      status: isStatus(status) ? status : DEFAULT_FILTERS.status,
      documentType: isDocument(type) ? type : DEFAULT_FILTERS.documentType,
    },
    selectedId,
  };
}

/** The link to the queue with these filters, and optionally one verification opened. */
export function verificationsHref(filters: VerificationFilters, selectedId: string | null = null): string {
  const params = new URLSearchParams();
  if (filters.status !== DEFAULT_FILTERS.status) params.set("status", filters.status);
  if (filters.documentType !== DEFAULT_FILTERS.documentType) params.set("type", filters.documentType);
  const query = params.toString();
  const path = selectedId ? `/${encodeURIComponent(selectedId)}` : "";
  return `#verifications${path}${query ? `?${query}` : ""}`;
}

/** The filters as the list endpoint takes them. */
export function listRequest(filters: VerificationFilters, cursor?: string): ListVerificationsRequest {
  const request: ListVerificationsRequest = { status: filters.status as VerificationStatus | "open" };
  if (filters.documentType !== "all") request.documentType = filters.documentType as IdDocumentType;
  if (cursor) request.cursor = cursor;
  return request;
}
